import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ArrowUp, Library, Square } from 'lucide-react';
import type { SyntheticEvent } from 'react';
import { usePrefilledText, type Prefill } from '../hooks/usePrefilledText';

type Props = {
  isBusy: boolean;
  prefill?: Prefill | null;
  onSend: (question: string) => void;
  onStop: () => void;
  /** Omit to leave out the Prompt library button. */
  onOpenLibrary?: (() => void) | undefined;
  /** The note beside the send button. */
  hint?: string;
  placeholder?: string | undefined;
  /** Only for screens where typing is the one thing to do, such as a new chat. */
  focusOnMount?: boolean;
};

/** The question box: Enter sends, Shift + Enter breaks the line, and the send button becomes Stop while answering. */
export function Composer({ isBusy, prefill = null, onSend, onStop, onOpenLibrary, hint = 'Every answer cites report, date and page', placeholder = 'Ask about forecasts, drivers or a report', focusOnMount = false }: Props) {
  const { text, setText, inputRef } = usePrefilledText(prefill, focusOnMount);
  const canSend = !isBusy && text.trim() !== '';

  const submit = (e: SyntheticEvent) => {
    e.preventDefault();
    const q = text.trim();
    if (!q || isBusy) return;
    onSend(q);
    setText('');
  };

  return (
    <Box component="form" onSubmit={submit} sx={cardSx}>
      <InputBase
        multiline
        fullWidth
        maxRows={8}
        inputRef={inputRef}
        placeholder={placeholder}
        value={text}
        onChange={(e) => { setText(e.target.value); }}
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) submit(e); }}
        slotProps={{ input: { 'aria-label': 'Ask OCBC research' } }}
        sx={{ px: 0.5, pt: 0.25, fontSize: '0.9375rem', '& textarea::placeholder': { color: 'text.secondary', opacity: 1 } }}
      />
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1, mt: 1.5 }}>
        {onOpenLibrary ? (
          <Button size="small" variant="outlined" onClick={onOpenLibrary} startIcon={<Library size={14} strokeWidth={1.75} />} sx={libraryButtonSx}>
            Prompt library
          </Button>
        ) : null}
        <Typography variant="caption" sx={{ ml: 'auto', color: 'text.secondary', display: { xs: isBusy ? 'block' : 'none', sm: 'block' } }} aria-live="polite">
          {isBusy ? 'Generating' : hint}
        </Typography>
        {isBusy ? (
          <IconButton aria-label="Stop answering" onClick={onStop} sx={{ ...roundSx, bgcolor: 'primary.main', color: 'primary.contrastText', '&:hover': { bgcolor: 'primary.dark' } }}>
            <Square size={11} fill="currentColor" />
          </IconButton>
        ) : (
          <IconButton type="submit" aria-label="Send question" disabled={!canSend} sx={sendSx}>
            <ArrowUp size={16} strokeWidth={2} />
          </IconButton>
        )}
      </Stack>
    </Box>
  );
}

const cardSx = {
  px: 1.75,
  pt: 1.75,
  pb: 1.25,
  borderRadius: 1.5,
  border: 1,
  borderColor: 'divider',
  bgcolor: 'background.paper',
  boxShadow: '0 1px 2px rgba(26, 26, 25, 0.04), 0 4px 16px rgba(26, 26, 25, 0.04)',
  transition: 'border-color 150ms, box-shadow 150ms',
  '&:focus-within': { borderColor: 'primary.light', boxShadow: '0 0 0 3px color-mix(in srgb, var(--mui-palette-primary-main) 12%, transparent)' },
} as const;

const libraryButtonSx = {
  borderRadius: 4,
  minHeight: 30,
  px: 1.25,
  color: 'text.primary',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  '&:hover': { bgcolor: 'surface.subtle', borderColor: 'surface.border' },
} as const;

const roundSx = { width: 30, height: 30, flexShrink: 0, borderRadius: '50%' } as const;

const sendSx = {
  ...roundSx,
  bgcolor: 'primary.main',
  color: 'primary.contrastText',
  transition: 'background-color 150ms, transform 150ms',
  '&:hover': { bgcolor: 'primary.dark', transform: 'scale(1.06)' },
  '&.Mui-disabled': { bgcolor: 'surface.sunken', color: 'text.secondary' },
} as const;
